'use client'

import { useEffect, useState } from 'react'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { Button } from '@/shared/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/shared/components/ui/tooltip'
import { Check, ChevronsUpDown, Info } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { ContactUserSelect, NO_ML_APPROVED_VALUE } from '@/features/stm/components/forms/ContactUserSelect'
import { MISSION_TRIP_LEADER_INFO } from '@/features/stm/constants/group-leader-tooltips'
import { getDisplayEmailValue, NO_EMAIL_AVAILABLE_OPTION } from '@/features/volunteers/schemas/volunteers.schema'

type GroupChecklistCardProps = {
  leaderAssigned: boolean
  leaderName: string
  leaderEmail: string
  leaderPhone: string
  missionTripLeaderUserId: number | null
  onMissionTripLeaderUserIdChange: (id: number | null) => void
  pretripInfoGiven: boolean
  depositReceived: boolean
  backgroundCheckComplete: boolean
  fullPaymentReceived: boolean
  postTripInfo: boolean
  dietaryRestrictions: boolean
  dietaryRestrictionsDetails: string
  onLeaderAssignedChange: (v: boolean) => void
  onLeaderNameChange: (v: string) => void
  onLeaderEmailChange: (v: string) => void
  onLeaderPhoneChange: (v: string) => void
  onPretripInfoGivenChange: (v: boolean) => void
  onDepositReceivedChange: (v: boolean) => void
  onBackgroundCheckCompleteChange: (v: boolean) => void
  onFullPaymentReceivedChange: (v: boolean) => void
  onPostTripInfoChange: (v: boolean) => void
  onDietaryRestrictionsChange: (v: boolean) => void
  onDietaryRestrictionsDetailsChange: (v: string) => void
  /** When true (e.g. Group Leader view), all fields are read-only */
  readOnly?: boolean
}

export function GroupChecklistCard({
  leaderAssigned,
  leaderName,
  leaderEmail,
  leaderPhone,
  missionTripLeaderUserId,
  onMissionTripLeaderUserIdChange,
  pretripInfoGiven,
  depositReceived,
  backgroundCheckComplete,
  fullPaymentReceived,
  postTripInfo,
  dietaryRestrictions,
  dietaryRestrictionsDetails,
  onLeaderAssignedChange,
  onLeaderNameChange,
  onLeaderEmailChange,
  onLeaderPhoneChange,
  onPretripInfoGivenChange,
  onDepositReceivedChange,
  onBackgroundCheckCompleteChange,
  onFullPaymentReceivedChange,
  onPostTripInfoChange,
  onDietaryRestrictionsChange,
  onDietaryRestrictionsDetailsChange,
  readOnly = false,
}: GroupChecklistCardProps) {
  const [missionTripLeaderInfoOpen, setMissionTripLeaderInfoOpen] = useState(false)
  const [isMissionLeaderEmailEditing, setIsMissionLeaderEmailEditing] = useState(false)
  const [missionLeaderEmailBeforeEdit, setMissionLeaderEmailBeforeEdit] = useState('')
  const [showNoEmailOptionSelected, setShowNoEmailOptionSelected] = useState(false)
  const [showNoEmailOptionManual, setShowNoEmailOptionManual] = useState(false)
  const inputClass = readOnly ? 'bg-muted cursor-not-allowed' : ''

  useEffect(() => {
    if (readOnly || missionTripLeaderUserId == null || missionTripLeaderUserId === NO_ML_APPROVED_VALUE) {
      setIsMissionLeaderEmailEditing(false)
    }
  }, [readOnly, missionTripLeaderUserId])

  return (
    <div className="space-y-4 pt-2">
      <div className="space-y-3 border rounded-lg p-4 bg-muted/30">
        <div className="flex flex-row items-center space-x-3 space-y-0">
          <Checkbox
            id="checklist-leader-assigned"
            checked={leaderAssigned}
            onCheckedChange={(v) => onLeaderAssignedChange(v === true)}
            disabled={readOnly}
          />
          <Label htmlFor="checklist-leader-assigned" className="font-normal cursor-pointer">
            Mission Trip Leader Assigned
          </Label>
        </div>
        {leaderAssigned && (
          <>
            <div className="space-y-2 sm:col-span-2">
              <div className="flex items-center gap-1.5">
                <Label htmlFor="checklist-leader-name">Mission Trip Leader *</Label>
                <Popover open={missionTripLeaderInfoOpen} onOpenChange={setMissionTripLeaderInfoOpen}>
                  <TooltipProvider delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <PopoverTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                            aria-label="Mission Trip Leader info"
                          >
                            <Info className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                      </TooltipTrigger>
                      <TooltipContent side="bottom" className="max-w-[260px] text-xs">
                        <p>{MISSION_TRIP_LEADER_INFO}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                  <PopoverContent side="bottom" className="max-w-[280px] text-sm">
                    <p>{MISSION_TRIP_LEADER_INFO}</p>
                  </PopoverContent>
                </Popover>
              </div>
              <ContactUserSelect
                value={missionTripLeaderUserId}
                onChange={(id) => {
                  if (readOnly) return
                  onMissionTripLeaderUserIdChange(id)
                  if (id == null) {
                    onLeaderNameChange('')
                    onLeaderEmailChange('')
                    onLeaderPhoneChange('')
                  } else if (id === NO_ML_APPROVED_VALUE) {
                    onLeaderNameChange('No ML Approved')
                    onLeaderEmailChange('')
                    onLeaderPhoneChange('')
                  }
                }}
                onVolunteerSelect={(v) => {
                  if (readOnly) return
                  onLeaderNameChange(v.fullname)
                  onLeaderEmailChange(v.email ?? '')
                  onLeaderPhoneChange(v.phone ?? '')
                }}
                onDisplayValueChange={(name) => {
                  if (readOnly) return
                  onLeaderNameChange(name)
                  if (missionTripLeaderUserId != null && missionTripLeaderUserId !== NO_ML_APPROVED_VALUE) {
                    onMissionTripLeaderUserIdChange(null)
                    onLeaderEmailChange('')
                    onLeaderPhoneChange('')
                  }
                }}
                allowNoMlApproved
                displayValue={leaderName || undefined}
                showDisplayValueWhenNoValue
                placeholder="Select or Approve No ML"
                disabled={readOnly}
              />
            </div>
            {missionTripLeaderUserId != null && missionTripLeaderUserId !== NO_ML_APPROVED_VALUE && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2 relative">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Label htmlFor="checklist-leader-email">Mission Trip Leader Email *</Label>
                    {!readOnly && (
                      !isMissionLeaderEmailEditing ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            setMissionLeaderEmailBeforeEdit(leaderEmail)
                            setIsMissionLeaderEmailEditing(true)
                          }}
                        >
                          Edit email
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            onLeaderEmailChange(missionLeaderEmailBeforeEdit)
                            setIsMissionLeaderEmailEditing(false)
                          }}
                        >
                          Cancel email edit
                        </Button>
                      )
                    )}
                  </div>
                  {(!readOnly && isMissionLeaderEmailEditing) ? (
                    <Popover open={showNoEmailOptionSelected} onOpenChange={setShowNoEmailOptionSelected}>
                      <PopoverTrigger asChild>
                        <div className="relative">
                          <Input
                            id="checklist-leader-email"
                            type="text"
                            value={leaderEmail}
                            onChange={(e) => onLeaderEmailChange((e.target as HTMLInputElement).value)}
                            className={cn(inputClass, 'pr-10')}
                          />
                          <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted"
                            onClick={() => setShowNoEmailOptionSelected((v) => !v)}
                            aria-label="Toggle email options"
                          >
                            <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                          </button>
                        </div>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                        <div className="p-1">
                          <button
                            type="button"
                            onMouseDown={(e) => {
                              e.preventDefault()
                              onLeaderEmailChange(NO_EMAIL_AVAILABLE_OPTION)
                              setShowNoEmailOptionSelected(false)
                            }}
                            className={cn(
                              "w-full flex items-center gap-2 px-2 py-2 text-sm rounded-sm text-left transition-colors",
                              "hover:bg-accent hover:text-accent-foreground",
                              (leaderEmail || '').trim().toLowerCase() === NO_EMAIL_AVAILABLE_OPTION.toLowerCase() && "bg-accent"
                            )}
                          >
                            <Check className={cn("h-4 w-4 shrink-0", (leaderEmail || '').trim().toLowerCase() === NO_EMAIL_AVAILABLE_OPTION.toLowerCase() ? "opacity-100 text-primary" : "opacity-0")} />
                            <span className="truncate">{NO_EMAIL_AVAILABLE_OPTION}</span>
                          </button>
                          <div className="py-2 text-center text-sm text-muted-foreground">Type to search by email.</div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  ) : (
                    <Input
                      id="checklist-leader-email"
                      type="text"
                      value={getDisplayEmailValue(leaderEmail)}
                      disabled={readOnly || !isMissionLeaderEmailEditing}
                      readOnly={readOnly}
                      className={readOnly ? 'bg-muted cursor-not-allowed' : inputClass}
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="checklist-leader-phone">Mission Trip Leader Phone</Label>
                  <Input
                    id="checklist-leader-phone"
                    type={readOnly ? 'text' : 'tel'}
                    value={leaderPhone}
                    onChange={(e) => onLeaderPhoneChange((e.target as HTMLInputElement).value)}
                    disabled={readOnly}
                    readOnly={readOnly}
                    className={readOnly ? 'bg-muted cursor-not-allowed' : inputClass}
                  />
                </div>
              </div>
            )}
            {missionTripLeaderUserId === null && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Label htmlFor="checklist-leader-email">Mission Trip Leader Email *</Label>
                    {!readOnly && leaderEmail.trim().length > 0 && (
                      !isMissionLeaderEmailEditing ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            setMissionLeaderEmailBeforeEdit(leaderEmail)
                            setIsMissionLeaderEmailEditing(true)
                          }}
                        >
                          Edit email
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-xs"
                          onClick={() => {
                            onLeaderEmailChange(missionLeaderEmailBeforeEdit)
                            setIsMissionLeaderEmailEditing(false)
                          }}
                        >
                          Cancel email edit
                        </Button>
                      )
                    )}
                  </div>
                  <div className="relative">
                    {(!readOnly && (isMissionLeaderEmailEditing || leaderEmail.trim().length === 0)) ? (
                      <Popover open={showNoEmailOptionManual} onOpenChange={setShowNoEmailOptionManual}>
                        <PopoverTrigger asChild>
                          <div className="relative">
                            <Input
                              id="checklist-leader-email"
                              type="text"
                              placeholder="email@example.com"
                              value={leaderEmail}
                              onChange={(e) => onLeaderEmailChange((e.target as HTMLInputElement).value)}
                              className={cn(inputClass, 'pr-10')}
                            />
                            <button
                              type="button"
                              className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded hover:bg-muted"
                              onClick={() => setShowNoEmailOptionManual((v) => !v)}
                              aria-label="Toggle email options"
                            >
                              <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                            </button>
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start" onOpenAutoFocus={(e) => e.preventDefault()}>
                          <div className="p-1">
                            <button
                              type="button"
                              onMouseDown={(e) => {
                                e.preventDefault()
                                onLeaderEmailChange(NO_EMAIL_AVAILABLE_OPTION)
                                setShowNoEmailOptionManual(false)
                              }}
                              className={cn(
                                "w-full flex items-center gap-2 px-2 py-2 text-sm rounded-sm text-left transition-colors",
                                "hover:bg-accent hover:text-accent-foreground",
                                (leaderEmail || '').trim().toLowerCase() === NO_EMAIL_AVAILABLE_OPTION.toLowerCase() && "bg-accent"
                              )}
                            >
                              <Check className={cn("h-4 w-4 shrink-0", (leaderEmail || '').trim().toLowerCase() === NO_EMAIL_AVAILABLE_OPTION.toLowerCase() ? "opacity-100 text-primary" : "opacity-0")} />
                              <span className="truncate">{NO_EMAIL_AVAILABLE_OPTION}</span>
                            </button>
                            <div className="py-2 text-center text-sm text-muted-foreground">Type to search by email.</div>
                          </div>
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <Input
                        id="checklist-leader-email"
                        type="text"
                        value={getDisplayEmailValue(leaderEmail)}
                        disabled={readOnly || (!isMissionLeaderEmailEditing && leaderEmail.trim().length > 0)}
                        readOnly={readOnly}
                        className={inputClass}
                      />
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="checklist-leader-phone">Mission Trip Leader Phone</Label>
                  <Input
                    id="checklist-leader-phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={leaderPhone}
                    onChange={(e) => onLeaderPhoneChange((e.target as HTMLInputElement).value)}
                    disabled={readOnly}
                    readOnly={readOnly}
                    className={inputClass}
                  />
                </div>
              </div>
            )}
          </>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 pt-2">
          <div className="flex flex-row items-center space-x-3 space-y-0">
            <Checkbox
              id="checklist-pretrip"
              checked={pretripInfoGiven}
              onCheckedChange={(v) => onPretripInfoGivenChange(v === true)}
              disabled={readOnly}
            />
            <Label htmlFor="checklist-pretrip" className="font-normal cursor-pointer">
              Pre-Trip Info Given
            </Label>
          </div>
          <div className="flex flex-row items-center space-x-3 space-y-0">
            <Checkbox
              id="checklist-deposit"
              checked={depositReceived}
              onCheckedChange={(v) => onDepositReceivedChange(v === true)}
              disabled={readOnly}
            />
            <Label htmlFor="checklist-deposit" className="font-normal cursor-pointer">
              Deposit Received
            </Label>
          </div>
          <div className="flex flex-row items-center space-x-3 space-y-0">
            <Checkbox
              id="checklist-bg-check"
              checked={backgroundCheckComplete}
              onCheckedChange={(v) => onBackgroundCheckCompleteChange(v === true)}
              disabled={readOnly}
            />
            <Label htmlFor="checklist-bg-check" className="font-normal cursor-pointer">
              Background Check
            </Label>
          </div>
          <div className="flex flex-row items-center space-x-3 space-y-0">
            <Checkbox
              id="checklist-full-payment"
              checked={fullPaymentReceived}
              onCheckedChange={(v) => onFullPaymentReceivedChange(v === true)}
              disabled={readOnly}
            />
            <Label htmlFor="checklist-full-payment" className="font-normal cursor-pointer">
              Full Payment
            </Label>
          </div>
          <div className="flex flex-row items-center space-x-3 space-y-0">
            <Checkbox
              id="checklist-post-trip"
              checked={postTripInfo}
              onCheckedChange={(v) => onPostTripInfoChange(v === true)}
              disabled={readOnly}
            />
            <Label htmlFor="checklist-post-trip" className="font-normal cursor-pointer">
              Post Trip Info
            </Label>
          </div>
          <div className="flex flex-row items-center space-x-3 space-y-0">
            <Checkbox
              id="checklist-dietary"
              checked={dietaryRestrictions}
              onCheckedChange={(v) => onDietaryRestrictionsChange(v === true)}
              disabled={readOnly}
            />
            <div className="flex items-center gap-2">
              <Label htmlFor="checklist-dietary" className="font-normal cursor-pointer">
                Dietary Restrictions
              </Label>
              <Popover>
                <TooltipProvider delayDuration={300}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="text-muted-foreground hover:text-foreground"
                          aria-label="Dietary restrictions behavior"
                        >
                          <Info className="h-4 w-4" />
                        </button>
                      </PopoverTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-[280px] text-xs">
                      When any group member has dietary restrictions, this may be auto-suggested as checked. Base Leaders and Group Leaders can still manually toggle and save.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <PopoverContent className="max-w-[300px] text-sm p-3" align="start">
                  When any group member has dietary restrictions, this may be auto-suggested as checked. Base Leaders and Group Leaders can still manually toggle and save.
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>
        {dietaryRestrictions && (
          <div className="pt-2 space-y-2">
            <Label htmlFor="checklist-dietary-details">Dietary restrictions details</Label>
            <Textarea
              id="checklist-dietary-details"
              placeholder="e.g. gluten-free, nut allergies, vegetarian..."
              value={dietaryRestrictionsDetails}
              onChange={(e) => onDietaryRestrictionsDetailsChange((e.target as HTMLTextAreaElement).value)}
              className={cn('min-h-[80px] resize-y', inputClass)}
              disabled={readOnly}
              readOnly={readOnly}
            />
          </div>
        )}
      </div>
    </div>
  )
}